import {AnyAction, Dispatch} from "redux";
import {RestAction, thunkDispatch} from "./TypeHelper";
import {DateTime} from "luxon";
import {SystemActions} from "../actions/SystemActions";
import {ContentType} from "./ContentTypes";
import {setCookie} from "./CommonCookies";

export const ErrorCode_NotAuthenticated = 4;

export function dispatchRestCall(dispatch: Dispatch, url: string, payload: any,
                                 beginAction: () => RestAction,
                                 successAction: (response: any) => RestAction,
                                 failAction: (response: Error) => RestAction) {
    dispatch((performRestCall(url, payload, beginAction, successAction, failAction) as unknown) as AnyAction);
}

export function performRestCallDirectly<T>(action: string, payload: any): Promise<T> {
    return performRawRestCall("/Mail3/WebService?a=" + action, payload);
}

export function performMail3RestCall<T>(action: string, payload: any): Promise<T> {
    return performRawRestCall("/Mail3/" + action, payload);
}

export class WebError {
    public readonly message: string;
    public readonly code: number;

    constructor(message: string, code: number) {
        this.message = message;
        this.code = code;
    }
}

export class AuthenticationError {
    public readonly message: string;

    constructor(message: string) {
        this.message = message;
    }
}

function validateResponseData(data: any) {
    if (data) {
        if (data.error) {
            throw new Error(data.error);
        }
        if (data.ErrorCode) {
            switch (data.ErrorCode) {
                case 4:
                    throw new AuthenticationError(data.Error);
            }
        }
        if (data.Error) {
            if (data.ErrorCode) {
                throw new WebError(data.Error, data.ErrorCode);
            }
            throw new Error(data.Error, {cause: data.ErrorCode});
        }
        if (data.errors && data.title) {
            throw new Error(data.title);
        }
    }
    return data;
}

export async function performRawRestCall<T>(url: string, payload: any, method = "POST", binaryData = false): Promise<T> {
    const body = JSON.stringify(payload);

    const options: RequestInit = {
        body,
        method: method,
        redirect: "follow",
        credentials: "same-origin",
        headers: {
            "Content-Type": "application/json",
            "Accepts": "application/json",
        },
    };

    const response = await fetch(url, options);

    if (binaryData) {
        const result = await response.text();
        return result as T;
    }
    let data: any = null;
    try {
        data = await response.json();
    } catch (e) {
        throw new Error(response.status + ": " + response.statusText);
    }
    return validateResponseData(data);
}

export async function performRawRestCallWithProgress<T>(options: {
    url: string;
    payload?: any;
    method?: "GET" | "POST" | "PUT" | "DELETE";
    onOpened?: () => void;
    onLoad?: () => void;
    onProgress?: (percentComplete: number) => void;
    contentType?: string;
}): Promise<T> {
    const data = await (new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.withCredentials = true;

        xhr.upload.onprogress = event => {
            if (event.lengthComputable && options.onProgress) {
                options.onProgress(event.loaded / event.total * 100);
            }
        };
        xhr.onreadystatechange = () => {
            // console.log("SYNC onreadystatechange", xhr.readyState);
            switch (xhr.readyState) {
                case 1:
                    options.onOpened!();
                    break;
                case 2:
                    // HEADERS RECEIVED
                    break;
                case 3:
                    // LOADING RESPONSE
                    break;
                case 4:
                    // DONE
                    break;
            }
        }
        xhr.onload = () => {
            if (options.onLoad) options.onLoad();
            try {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve(JSON.parse(xhr.responseText));
                } else {
                    reject(convertResponseToError(xhr));
                }
            } catch (e) {
                reject(e);
            }
        };
        xhr.onerror = event => {
            console.error(event);
            reject("Network error");
        };

        const contentType = options.contentType ?? "application/json";
        const method = options.method ?? "POST";

        xhr.open(method, options.url, true);
        if (contentType) {
            xhr.setRequestHeader("Content-Type", contentType);
        }
        xhr.setRequestHeader("Accepts", "application/json");

        if (contentType === "application/json") {
            xhr.send(JSON.stringify(options.payload));
        } else {
            xhr.send(options.payload);
        }
    }));
    return validateResponseData(data);
}

export function performRestCall(action: string, payload: any,
                                beginAction: () => RestAction,
                                successAction: (response: any) => RestAction,
                                failAction: (response: Error) => RestAction) {

    return performRestCall_Internal("/Mail3/WebService?a=" + action, payload, beginAction, successAction, failAction);
}

function convertResponseToError(response: Response | XMLHttpRequest): Error {
    switch (response.status) {
        case 502:
            return new Error("server temporarily unavailable");
        case 504:
            return new Error("connection timed out");
        case 400:
            return new Error("badly formatted request");
        case 404:
            return new Error("not found");
    }
    return new Error(response.statusText || ("unknown error code: " + response.status));
}

function performRestCall_Internal(url: string, payload: any,
                                  beginAction: () => RestAction,
                                  successAction: (response: any) => RestAction,
                                  failAction: (response: Error) => RestAction) {

    return async (dispatch: Dispatch) => {
        thunkDispatch(dispatch, beginAction());

        try {
            const body = JSON.stringify(payload);

            const options: RequestInit = {
                body,
                method: "POST",
                redirect: "follow",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                    "Accepts": "application/json",
                }
            };

            const response = await fetch(url, options);
            if (response.status === 401 && url.indexOf("PerformFullLogin") === -1) {
                SystemActions.logout(dispatch);
            } else if (response.status === 200) {
                const data = await response.json();

                if (data.Cookies) {
                    for (const cookie of data.Cookies) {
                        setCookie(cookie.Name, cookie.Value, {
                            path: '/',
                            sameSite: "strict",
                            secure: true,
                            expires: DateTime.local().plus({months: 1}).toJSDate()
                        });
                    }
                }
                if (data.ErrorCode === ErrorCode_NotAuthenticated && url.indexOf("PerformFullLogin") === -1) {
                    SystemActions.logout(dispatch);
                } else if (data.Error) {
                    thunkDispatch(dispatch, failAction(new Error(data.Error)));
                } else {
                    thunkDispatch(dispatch, successAction(data));
                }

            } else {
                thunkDispatch(dispatch, failAction(convertResponseToError(response)));
            }

        } catch (err: any) {
            if (err.message && err.message.indexOf("NetworkError when attempting to fetch resource") > -1) {
                thunkDispatch(dispatch, failAction(new Error("Please check your internet connection")));
            } else {
                thunkDispatch(dispatch, failAction(err));
            }
        }
    }
}

export function isSecureConnection() {
    return window.location.protocol === "https:";
}

export async function sendFormData(url: string, formData: FormData, onProgress: (percentComplete: number) => void): Promise<any> {
    return await performRawRestCallWithProgress<any>({url, payload: formData, method: "POST", contentType: "", onProgress});
}

export async function performFetch(url: string, payload?: any) {

    const options: RequestInit = {
        body: payload ? JSON.stringify(payload) : undefined,
        method: payload ? "POST" : "GET",
        redirect: "follow",
        credentials: "same-origin",
        headers: {
            "Content-Type": "application/json",
            "Accepts": "application/json",
        }
    };

    const response = await fetch(url, options);
    if (response.status === 200) {
        const contentType = response.headers.get("content-type") || ContentType.Json;

        if (contentType === ContentType.BinaryStream) {
            return response.blob();
        }

        const data = await response.json();
        if (data && data.Error) {
            throw new Error(data.Error);
        }

        return data;
    } else {
        throw convertResponseToError(response);
    }
}

export async function fetchFileData(url: string) {
    const options: RequestInit = {
        method: "GET",
        redirect: "follow",
        credentials: "same-origin",
        headers: {}
    };
    const response = await fetch(url, options);
    if (response.status === 200) {
        return response.blob();
    } else {
        throw convertResponseToError(response);
    }
}
